import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuxiliaryDataFieldType } from '@vendasta/account-group';
import { AuxiliaryDataFieldSchemaInterface } from '@vendasta/account-group/lib/_internal/interfaces/fields.interface';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { combineLatest, iif, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { AuxiliaryDataCurrencyInputDisplayService } from '../../filters/definitions/dynamic-options/auxiliary-data-currency-input/auxiliary-data-currency-input-display.service';
import { AuxiliaryDataDropdownDisplayService } from '../../filters/definitions/dynamic-options/auxiliary-data-dropdown-options/auxiliary-data-dropdown-display.service';

const ObjectTypeTextMap = new Map([
  ['business', 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OBJECT_TYPE.ACCOUNT_GROUP'],
  ['user', 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OBJECT_TYPE.USER'],
  ['product', 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OBJECT_TYPE.PRODUCT'],
  ['order', 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OBJECT_TYPE.SALES_ORDER'],
  ['contact', 'TODO Contact'],
  ['company', 'TODO Company'],
]);

@Injectable({ providedIn: 'root' })
export class ModifyAuxiliaryDataService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    protected readonly schemaService: SchemaService,
    protected readonly datePipe: DatePipe,
    protected readonly dropdownInputDisplay: AuxiliaryDataDropdownDisplayService,
    protected readonly currencyInputDisplay: AuxiliaryDataCurrencyInputDisplayService,
  ) {
    super();
  }

  private getFieldSchema(
    objectType: ObjectType,
    fieldId: string,
    partnerId: string,
  ): Observable<AuxiliaryDataFieldSchemaInterface> {
    return this.schemaService
      .getFieldSchema(objectType, { fieldId: fieldId, partnerId: partnerId })
      .pipe(map((response) => response.fieldSchema));
  }

  private getFieldValueDisplay(
    fieldSchema$: Observable<AuxiliaryDataFieldSchemaInterface>,
    value: any,
    operation: string,
  ): Observable<string> {
    if (value === undefined || value === '') {
      return of(this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.EMPTY_VALUE'));
    }

    return fieldSchema$.pipe(
      switchMap((fieldSchema) => {
        switch (fieldSchema.fieldType) {
          case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING:
          case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER:
            return of(`<strong>${value}</strong>`);
          case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN:
            this.dropdownInputDisplay.initialize(
              fieldSchema.dropdownOptions.map((opt) => ({
                label: opt?.label || '',
                value: opt?.value || '',
              })),
            );
            return this.dropdownInputDisplay.getDisplay(value);
          case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE:
            // custom date fields are just dates, not date+time
            return of(`<strong>${this.datePipe.transform(value, 'MMM dd, yyyy')}</strong>`);
          case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY:
            if (['DIVIDE', 'MULTIPLY'].includes(operation)) {
              return of(`<strong>${value}</strong>`);
            }

            this.currencyInputDisplay.initialize(fieldSchema.currencyCode);
            return this.currencyInputDisplay.getDisplay(value);
          default:
            return of(this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.EMPTY_VALUE'));
        }
      }),
    );
  }

  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    const data = activity.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const fieldSchema$ = this.getFieldSchema(data.object_type, data.field_id, namespace).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    let message = fieldSchema$.pipe(
      switchMap((fieldSchema) =>
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.ACTIVITY_FAILURE', {
          fieldName: fieldSchema.fieldName.toLowerCase(),
          objectType: this.translateService.instant(ObjectTypeTextMap.get(data.object_type)),
        }),
      ),
    );
    if (activity?.error) {
      return this.tableEntry(message, this.emptyBody());
    }
    const activityData = activity.activityData ? JSON.parse(activity.activityData) : {};
    const fieldValueDisplay$ = this.getFieldValueDisplay(fieldSchema$, data.value, data.operation);
    const previousValueDisplay$ = this.getFieldValueDisplay(fieldSchema$, activityData.previous_value, '');
    const newValueDisplay$ = this.getFieldValueDisplay(fieldSchema$, activityData.new_value, '');

    message = fieldSchema$.pipe(
      withLatestFrom(combineLatest([fieldValueDisplay$, previousValueDisplay$, newValueDisplay$])),
      switchMap(([fieldSchema, [fieldValueDisplay, previousValueDisplay, newValueDisplay]]) =>
        this.translateService.stream(
          `AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OPERATIONS_ACTIVITY_TEXT.${data.operation}`,
          {
            fieldName: fieldSchema.fieldName.toLowerCase(),
            objectType: this.translateService.instant(ObjectTypeTextMap.get(data.object_type)),
            value: fieldValueDisplay,
            previousValue: previousValueDisplay,
            newValue: newValueDisplay,
          },
        ),
      ),
    );

    return this.tableEntry(message, this.getDefaultStepColumnBody(namespace, activity.workflowStep!, automation));
  }

  getAutomationNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    let message = this.translateService.stream(
      'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.ACCOUNT_DATA.ACTION_TITLE',
    );

    if (step.data) {
      const data = JSON.parse(step.data);
      const fieldSchema$ = this.getFieldSchema(data.object_type, data.field_id, namespace).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      const fieldValueDisplay$ = this.getFieldValueDisplay(fieldSchema$, data.value, data.operation);
      const isArchived$ = fieldSchema$.pipe(map((fieldSchema) => !!fieldSchema.archived));

      message = fieldSchema$.pipe(
        withLatestFrom(fieldValueDisplay$),
        switchMap(([fieldSchema, fieldValueDisplay]) =>
          this.translateService.stream(
            `AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.OPERATIONS_NODE_TEXT.${data.operation}`,
            {
              fieldName: fieldSchema.fieldName.toLowerCase(),
              objectType: this.translateService.instant(ObjectTypeTextMap.get(data.object_type)),
              value: fieldValueDisplay,
            },
          ),
        ),
      );

      return isArchived$.pipe(
        switchMap((isArchived) =>
          iif(
            () => isArchived,
            this.nodeDisplayData(
              message,
              this.validationErrorsDetected(
                step,
                this.translateService.instant(
                  'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.ARCHIVED_CONFIG_ERROR',
                ),
                true,
              ),
            ),
            this.nodeDisplayData(message, this.noValidationErrorsDetected()),
          ),
        ),
      );
    }

    return this.nodeDisplayData(
      message,
      this.validationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.DEFAULT_CONFIG_ERROR'),
        true,
      ),
    );
  }

  getTemplateNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    const message = this.buildMessageFromTemplateData(data);
    return this.nodeDisplayData(
      message,
      this.validationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.DEFAULT_CONFIG_ERROR'),
        true,
      ),
    );
  }

  private buildMessageFromTemplateData(_data: any): Observable<string> {
    const message = this.translateService.stream(
      'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.ACCOUNT_DATA.ACTION_TITLE',
    );
    return message;
  }
}
